import React, { useState, useEffect } from "react";
import {
  faPlay,
  faStop,
  faRoute,
  faTriangleExclamation,
  faCarBurst,
  faRoad,
  faClock,
  faCamera,
  faMicrophone,
  faBullhorn,
  faLocationCrosshairs,
  faSignal,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useDriverTrip } from "../context/DriverTripContext";

// Dummy Routes with stops and ETA
const dummyRoutes = [
  {
    id: "R1",
    name: "Route A → B",
    distance: "50 km",
    stops: Array.from({ length: 25 }, (_, i) => ({
      name: i === 0 ? "Origin: A" : i === 24 ? "Destination: B" : `Stop ${i}`,
      eta: (() => {
        const h = 9 + Math.floor(i / 4);
        const m = (i * 15) % 60;
        const suffix = h >= 12 ? "PM" : "AM";
        const hour12 = h > 12 ? h - 12 : h;
        return `${hour12.toString().padStart(2, "0")}:${m
          .toString()
          .padStart(2, "0")} ${suffix}`;
      })(),
      reached: false,
    })),
  },
  {
    id: "R2",
    name: "Route A → C",
    distance: "40 km",
    stops: Array.from({ length: 18 }, (_, i) => ({
      name: i === 0 ? "Origin: A" : i === 17 ? "Destination: C" : `Stop ${i}`,
      eta: (() => {
        const h = 9 + Math.floor(i / 4);
        const m = (i * 20) % 60;
        const suffix = h >= 12 ? "PM" : "AM";
        const hour12 = h > 12 ? h - 12 : h;
        return `${hour12.toString().padStart(2, "0")}:${m
          .toString()
          .padStart(2, "0")} ${suffix}`;
      })(),
      reached: false,
    })),
  },
];

export default function DriverControl() {
  const { trip = {}, startTrip, endTrip, addEvent, toggleLate } =
    useDriverTrip() || {};

  const [route, setRoute] = useState(null);
  const [startKM, setStartKM] = useState("");
  const [endKM, setEndKM] = useState("");
  const [elapsed, setElapsed] = useState(0);
  const [late, setLate] = useState(false);
  const [status, setStatus] = useState("IDLE"); // IDLE | RUNNING | ENDED
  const [stops, setStops] = useState([]);
  const [tripEnded, setTripEnded] = useState(false);
  const [currentTime, setCurrentTime] = useState(new Date());

  // Timer for running trip
  useEffect(() => {
    let timer;
    if (status === "RUNNING") {
      timer = setInterval(() => setElapsed((e) => e + 1), 1000);
    }
    return () => clearInterval(timer);
  }, [status]);

  // Digital clock update
  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  // Update stops when route selected
  useEffect(() => {
    if (route) setStops([...route.stops]);
  }, [route]);

  const formatTime = (sec) => `${Math.floor(sec / 60)}m ${sec % 60}s`;

  const formatClock = (date) => {
    let h = date.getHours();
    const m = date.getMinutes();
    const s = date.getSeconds();
    const ampm = h >= 12 ? "PM" : "AM";
    h = h % 12;
    h = h ? h : 12; // 0 should be 12
    return `${h.toString().padStart(2, "0")}:${m
      .toString()
      .padStart(2, "0")}:${s.toString().padStart(2, "0")} ${ampm}`;
  };

  const markReached = (index) => {
    setStops((prev) =>
      prev.map((stop, i) => (i === index ? { ...stop, reached: true } : stop))
    );
    addEvent?.(`Reached ${stops[index].name}`);
    adjustETA(index);
  };

  const stopColor = (stop, index) => {
    if (index === 0)
      return late ? "text-blue-400 font-bold" : "text-green-400 font-bold";
    if (stop.reached) return late ? "text-orange-300" : "text-blue-300";
    return "text-gray-400";
  };

  const adjustETA = (index) => {
    const newStops = stops.map((s, i) => {
      if (i <= index) return s;
      const oldTime = s.eta.split(/[: ]/);
      let h = parseInt(oldTime[0]);
      const m = parseInt(oldTime[1]);
      const ampm = oldTime[2];
      const newMin = m + (late ? 5 : 0);
      const addHour = Math.floor(newMin / 60);
      h = h + addHour;
      const minFinal = newMin % 60;
      const hour12 = h > 12 ? h - 12 : h;
      return { ...s, eta: `${hour12.toString().padStart(2, "0")}:${minFinal.toString().padStart(2, "0")} ${ampm}` };
    });
    setStops(newStops);
  };

  // ------------------------
  // MAIN RENDER
  // ------------------------
  if (tripEnded) {
    return (
      <div className="min-h-screen bg-black text-white flex flex-col items-center justify-center gap-6 p-4">
        <div className="text-2xl font-bold text-green-400">
          Thank You. Take a Break {trip.driverName || "Driver"}!
        </div>
        <button
          className="bg-red-600 px-6 py-3 rounded-lg font-bold"
          onClick={() => {
            setTripEnded(false);
            setRoute(null);
            setStatus("IDLE");
            toggleLate?.(false);
            endTrip?.("");
          }}
        >
          Bye
        </button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white p-3 flex flex-col gap-3">

      {/* HEADER */}
      <div className="flex justify-between items-center">
        <div className="text-lg font-semibold">🚦 Driver Eye</div>
        <button className="bg-red-600 px-4 py-2 rounded-md w-24 text-sm font-bold">
          <FontAwesomeIcon icon={faSignal} /> SOS
        </button>
      </div>

      {/* ROUTE SELECTION */}
      {!route && (
        <div className="grid gap-3">
          <div className="text-sm text-gray-400">Select Route</div>
          {dummyRoutes.map((r) => (
            <button
              key={r.id}
              onClick={() => setRoute(r)}
              className="bg-gray-900 p-4 rounded-lg flex justify-between items-center"
            >
              <div>
                <div className="font-semibold">{r.name}</div>
                <div className="text-xs text-gray-400">{r.distance}</div>
              </div>
              <FontAwesomeIcon icon={faRoute} />
            </button>
          ))}
        </div>
      )}

      {/* TRIP CARD */}
      {route && (
        <div className="bg-gray-900 rounded-xl p-4 space-y-3">

          {/* Route Name & Status */}
          <div className="flex justify-between items-center">
            <div>
              <div className="font-semibold">{route.name}</div>
              <div className="text-xs text-gray-400">
                Status: {status} | Current Time:{" "}
                <span className="font-mono">{formatClock(currentTime)}</span>
              </div>
            </div>
            {status === "RUNNING" && (
              <div className="text-sm text-green-400">
                ⏱ {formatTime(elapsed)}
              </div>
            )}
          </div>

          {/* Stops List */}
          <div className="space-y-2 max-h-72 overflow-y-auto">
            {stops.map((stop, index) => (
              <div
                key={index}
                className={`flex justify-between items-center p-2 rounded ${
                  stop.reached ? "bg-gray-800" : "bg-gray-900"
                }`}
              >
                <div className={stopColor(stop, index)}>{stop.name}</div>
                <div className={`text-xs ${stop.reached ? "opacity-60" : "opacity-90"}`}>
                  {stop.reached ? "Reached" : stop.eta}
                </div>
                {!stop.reached && status === "RUNNING" && (
                  <button
                    className="bg-green-800 px-2 py-1 rounded text-xs w-8 h-8"
                    onClick={() => markReached(index)}
                  >
                    ✅
                  </button>
                )}
              </div>
            ))}
          </div>

          {/* KM INPUT */}
          {status === "IDLE" && (
            <input
              placeholder="Start KM Reading"
              value={startKM}
              onChange={(e) => setStartKM(e.target.value)}
              className="w-full bg-black border border-gray-700 rounded p-2"
            />
          )}

          {status === "ENDED" && (
            <input
              placeholder="End KM Reading"
              value={endKM}
              onChange={(e) => setEndKM(e.target.value)}
              className="w-full bg-black border border-gray-700 rounded p-2"
            />
          )}

          {/* START / END */}
          {status === "IDLE" && (
            <button
              onClick={() => {
                setStatus("RUNNING");
                startTrip?.(route.id, startKM);
              }}
              className="w-full bg-green-600 py-3 rounded-lg text-lg font-bold"
            >
              <FontAwesomeIcon icon={faPlay} /> Start Trip
            </button>
          )}

          {status === "RUNNING" && (
            <button
              onClick={() => {
                setStatus("ENDED");
                endTrip?.(route.id, endKM);
                setTripEnded(true);
              }}
              className="w-full bg-yellow-600 py-3 rounded-lg text-lg font-bold"
            >
              <FontAwesomeIcon icon={faStop} /> Mark Reached
            </button>
          )}
        </div>
      )}

      {/* QUICK EVENTS */}
      {status === "RUNNING" && (
        <div className="grid grid-cols-2 gap-3">
          {[
            { label: "Delay", icon: faClock },
            { label: "Congestion", icon: faRoad },
            { label: "Hazard", icon: faTriangleExclamation },
            { label: "Accident", icon: faCarBurst },
            { label: "Vehicle Glitch", icon: faTriangleExclamation },
            { label: "Suspicion", icon: faTriangleExclamation },
          ].map((e) => (
            <button
              key={e.label}
              onClick={() => addEvent?.(e.label)}
              className="bg-gray-800 p-4 rounded-xl text-sm flex flex-col items-center gap-2"
            >
              <FontAwesomeIcon icon={e.icon} size="lg" />
              {e.label}
            </button>
          ))}
        </div>
      )}

      {/* COMMUNICATION */}
      {status === "RUNNING" && (
        <div className="grid grid-cols-3 gap-3">
          <button className="bg-gray-800 p-4 rounded-xl">
            <FontAwesomeIcon icon={faMicrophone} />
            <div className="text-xs">Voice</div>
          </button>
          <button className="bg-gray-800 p-4 rounded-xl">
            <FontAwesomeIcon icon={faCamera} />
            <div className="text-xs">Photo</div>
          </button>
          <button className="bg-gray-800 p-4 rounded-xl">
            <FontAwesomeIcon icon={faBullhorn} />
            <div className="text-xs">Announce</div>
          </button>
        </div>
      )}

      {/* LATE TOGGLE */}
      {status === "RUNNING" && (
        <button
          onClick={() => {
            setLate(!late);
            toggleLate?.(!late);
          }}
          className={`py-3 rounded-lg font-semibold ${
            late ? "bg-red-600" : "bg-gray-700"
          }`}
        >
          Will Be Late: {late ? "YES" : "NO"}
        </button>
      )}

      {/* LOCATION */}
      {status === "RUNNING" && (
        <button
          className="bg-gray-900 p-3 rounded-lg flex items-center justify-center gap-2"
          onClick={() => addEvent?.("Location Captured")}
        >
          <FontAwesomeIcon icon={faLocationCrosshairs} />
          Capture Location
        </button>
      )}
    </div>
  );
}
